/**
 * titleReveal — a chat's real name arriving, rather than snapping in.
 *
 * A new chat is called "New Session" until the model writes its title, which
 * lands mid-answer, on the completion event. Done as a plain state swap that
 * is a hard flicker — in the sidebar row and in the header crumb at once, at
 * the exact moment the reader is looking at the answer instead.
 *
 * The rule this encodes: an INITIAL value is not a reveal. A row painted for
 * the first time — a chat loaded from history, a page reload, a search
 * filtering the list — shows its title immediately. Only a title that
 * CHANGES under an already-rendered element animates.
 *
 * The motion is a gentle hand-off, three phases, all CSS transitions on one
 * node so nothing reflows and no two strings are ever legible at once:
 *
 *   out    → the old title fades and lifts away        (OUT_MS)
 *   enter  → the new title is placed, still invisible and sitting low
 *            (one frame, ENTER_MS, so the transition has a start state)
 *   idle   → it settles into place                     (IN_MS, by CSS)
 *
 * This replaced a type-in, which read as a machine typing rather than as a
 * name arriving — and which the header crumb needed only because it took
 * plain strings. The crumb now takes a node, so both surfaces run the same
 * three phases and land together.
 */

import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../answerReveal';

export type RevealPhase = 'idle' | 'out' | 'enter';

export interface TitleReveal {
  /** What to render right now — the old title until the swap. */
  text: string;
  phase: RevealPhase;
}

/** How long the old title takes to leave. */
export const OUT_MS = 140;
/** The single frame the new title is held at its start state. */
export const ENTER_MS = 30;
/** How long the new title takes to settle. Mirrored in RevealingTitle's CSS. */
export const IN_MS = 320;

/**
 * The prefix to show at a given point through a type-in.
 *
 * No longer used by the reveal — kept because it is the one piece of this
 * module with a use elsewhere: cutting a title on a word boundary rather
 * than mid-word ("Notice" rather than "Notice peri").
 */
export function typedPrefix(text: string, progress: number): string {
  if (progress >= 1) return text;
  if (progress <= 0) return '';
  const target = Math.ceil(text.length * progress);
  const space = text.lastIndexOf(' ', target);
  // Only snap back to a word boundary when it is close; otherwise a long
  // first word would leave the title empty for most of the animation.
  return text.slice(0, space > 0 && target - space <= 12 ? space : target);
}

/**
 * A title, revealed when it changes.
 *
 * Returns the text to render and the phase; the phase is what the rendering
 * component turns into opacity and offset, so the same hook drives the
 * sidebar row and the header crumb identically.
 */
export function useTitleReveal(title: string): TitleReveal {
  const [reveal, setReveal] = useState<TitleReveal>({ text: title, phase: 'idle' });
  /** The last title we have finished showing — the thing `title` changes FROM. */
  const shownRef = useRef(title);

  useEffect(() => {
    if (title === shownRef.current) return;

    const previous = shownRef.current;
    shownRef.current = title;

    // An empty title is a cleared chat, not a rename; and a first title on an
    // element that had none is it painting itself. Neither is a reveal.
    if (!title || !previous || prefersReducedMotion()) {
      setReveal({ text: title, phase: 'idle' });
      return;
    }

    const timers: number[] = [];
    setReveal({ text: previous, phase: 'out' });

    timers.push(window.setTimeout(() => {
      // The new text arrives already faded and low, so the settle below is a
      // transition the browser can actually run.
      setReveal({ text: title, phase: 'enter' });
      timers.push(window.setTimeout(() => {
        setReveal({ text: title, phase: 'idle' });
      }, ENTER_MS));
    }, OUT_MS));

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [title]);

  return reveal;
}
